import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Profile";

function AllUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch all users ───────────────────────────────────────────────────────
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/GetUserProfile`, {
        headers: {
          Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log("📦 GetUserProfile (all) response:", data);
      const list = Array.isArray(data) ? data : (data.data ?? data.users ?? []);
      setUsers(list);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ── Toggle slnAccess ──────────────────────────────────────────────────────
  const handleToggleAccess = async (user) => {
    const userId = user.userId;
    const currentAccess = user.slnAccess ?? false;
    const newAccess = !currentAccess;

    const result = await Swal.fire({
      title: `${newAccess ? "Grant" : "Revoke"} SLN Access?`,
      html: `<strong>${user.name ?? userId}</strong> will have SLN access <strong>${newAccess ? "enabled" : "disabled"}</strong>.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newAccess ? "#198754" : "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newAccess ? "Yes, Grant Access" : "Yes, Revoke Access",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    setTogglingId(userId);
    try {
      const payload = { userId, slnAccess: newAccess };
      console.log("📤 UpdateProfile payload:", payload);

      const res = await fetch(`${BASE_URL}/UpdateProfile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === userId ? { ...u, slnAccess: newAccess } : u,
        ),
      );

      await Swal.fire({
        title: newAccess ? "Access Granted!" : "Access Revoked!",
        text:
          data?.message ??
          `SLN access has been ${newAccess ? "enabled" : "disabled"}.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ UpdateProfile error:", err);
      await Swal.fire({
        title: "Update Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setTogglingId(null);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // status badge colour
  const statusBadge = (status) => {
    const map = {
      active: "bg-success-subtle text-success border-success-subtle",
      pending: "bg-warning-subtle text-warning border-warning-subtle",
      inactive: "bg-secondary-subtle text-secondary border-secondary-subtle",
      blocked: "bg-danger-subtle text-danger border-danger-subtle",
    };
    return (
      map[status?.toLowerCase()] ?? "bg-light text-dark border-secondary-subtle"
    );
  };

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(u.name ?? "").toLowerCase().includes(q) ||
      String(u.email ?? "").toLowerCase().includes(q) ||
      String(u.phone ?? "").toLowerCase().includes(q) ||
      String(u.userId ?? "").toLowerCase().includes(q) ||
      String(u.status ?? "").toLowerCase().includes(q) ||
      String(u.referralCode ?? "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    All Users{" "}
                    {!loading && !error && (
                      <span className="text-secondary">({users.length})</span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Manage Users / SLN Access Control
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching users…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load users.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchUsers}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, email, phone, user ID, status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredUsers.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredUsers.length)} of{" "}
                {filteredUsers.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="w-10 py-2">No.</th>
                    <th className="lh-1 w-60 py-2">
                      Details
                      <i className="bi bi-info-circle ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Displaying Information as per Record
                      </span>
                    </th>
                    <th className="lh-1 w-30 py-2">
                      SLN Scan Access
                      <i className="bi bi-shield-lock ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Toggle to Grant / Revoke
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-5 text-secondary"
                      >
                        <i className="bi bi-people fs-2 d-block mb-2"></i>
                        {users.length === 0
                          ? "No users found."
                          : "No users match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user, idx) => {
                      // ── exact field names from API ──
                      const userId = user.userId;
                      const name = user.name ?? "—";
                      const email = user.email ?? "—";
                      const phone = user.phone ?? null;
                      const profilePic = user.profilePic ?? null;
                      const status = user.status ?? null;
                      const referralCode = user.referralCode ?? null;
                      const referredBy = user.referredBy || null;
                      const firstPaymentDone = user.firstPaymentDone ?? false;
                      const isPhoneVerified = user.isPhoneVerified ?? false;
                      const isEmailVerified = user.isEmailVerified ?? false;
                      const slnAccess = user.slnAccess ?? false;
                      const createdAt = user.createdAt ?? null;
                      const updatedAt = user.updatedAt ?? null;
                      const isToggling = togglingId === userId;

                      return (
                        <tr
                          key={userId}
                          style={{ opacity: isToggling ? 0.6 : 1 }}
                        >
                          <td>{startIdx + idx + 1}.</td>

                          {/* ── User Details ── */}
                          <td className="lh-sm py-4">
                            <div className="d-flex align-items-start gap-3">
                              {/* Avatar */}
                              {profilePic ? (
                                <img
                                  src={profilePic}
                                  alt={name}
                                  style={{
                                    width: 44,
                                    height: 44,
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    border: "2px solid #dee2e6",
                                  }}
                                  onError={(e) =>
                                    (e.target.style.display = "none")
                                  }
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: "20%",
                                    background: "#e9ecef",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "#6c757d",
                                    flexShrink: 0,
                                    border: "2px solid #dee2e6",
                                  }}
                                >
                                  {getInitials(name)}
                                </div>
                              )}

                              {/* Info */}
                              <div>
                                <span className="text-dark fw-semibold">
                                  {name}
                                </span>
                                {status && (
                                  <span
                                    className={`badge border ms-2 px-2 py-1 rounded-3 small text-capitalize ${statusBadge(status)}`}
                                  >
                                    {status}
                                  </span>
                                )}
                                <br />
                                <span className="text-dark">
                                  <strong>User ID:</strong>
                                </span>{" "}
                                <code className="text-secondary">{userId}</code>
                                <br />
                                <span className="text-dark">
                                  <strong>Email:</strong>
                                </span>{" "}
                                {email}
                                <br />
                                <span className="text-dark">
                                  <strong>Phone:</strong>
                                </span>{" "}
                                {phone ?? "—"}
                                <br />
                                <span className="text-dark">
                                  <strong>Joined:</strong>
                                </span>{" "}
                                {formatDate(createdAt)}
                                <br />
                                <span className="text-dark">
                                  <strong>Updated:</strong>
                                </span>{" "}
                                {formatDate(updatedAt)}
                              </div>
                            </div>
                          </td>

                          {/* ── SLN Access Toggle ── */}
                          <td>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`sln-${userId}`}
                                checked={slnAccess}
                                disabled={isToggling}
                                onChange={() => handleToggleAccess(user)}
                                style={{
                                  cursor: "pointer",
                                  width: "2.5em",
                                  height: "1.3em",
                                }}
                              />
                              <label
                                className="form-check-label ms-2 small"
                                htmlFor={`sln-${userId}`}
                                style={{ cursor: "pointer" }}
                              >
                                {isToggling ? (
                                  <span
                                    className="spinner-border spinner-border-sm text-primary"
                                    role="status"
                                  />
                                ) : slnAccess ? (
                                  <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 rounded-3">
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                    Granted
                                  </span>
                                ) : (
                                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 rounded-3">
                                    <i className="bi bi-x-circle-fill me-1"></i>
                                    Revoked
                                  </span>
                                )}
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && filteredUsers.length > 0 && totalPages > 1 && (
            <nav aria-label="Users pagination" className="mt-3">
              <ul className="pagination justify-content-center mb-0 flex-wrap">
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {getPageNumbers().map((num) => (
                  <li
                    key={num}
                    className={`page-item ${num === safePage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default AllUsers;
